import { useState, useEffect, useMemo } from 'react';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import { PageHeader, ActionsMenu } from '@evertrue/et-components';
import BulkDeleteEmailModal from './components/bulk-delete-email-modal';
import OutreachTabGroup from '../outreach-tab-group';
import EmailTabGroup from './email-tab-group';
import EmailDraftController from './drafts/email-draft-controller';
import ScheduledEmailController from './scheduled/scheduled-email-controller';
import ThreadController from './threads/thread-controller';
import EmailerDrawerButton from 'components/drawer/components/emailer-drawer-button';
import { useEmailDrafts } from './drafts/email-drafts-context';
import { useScheduledEmails } from './scheduled/scheduled-email-context';
import { useEmailThreads } from './threads/email-threads-context';
import { useOutreachFilterProvider } from './contexts/outreach-filter-context';
import OutreachFilters from './components/outreach-filters';

const EmailController = ({ emailTab }) => {
  const {
    replyStatus,
    setReplyStatus,
    createdDateRange,
    setCreatedDateRange,
    scheduledDateRange,
    setScheduledDateRange,
    sentDateRange,
    setSentDateRange,
    replyDateRange,
    setReplyDateRange,
    selectedFilters,
    setSelectedFilters,
    filterOptions,
    setFilterOptions,
    selectedEmailAddresses,
    setSelectedEmailAddresses,
    selectedNames,
    setSelectedNames,
    filterType,
    setFilterType,
  } = useOutreachFilterProvider();
  const [selectedEmails, setSelectedEmails] = useState([]);

  // Check if any filters are currently applied
  const hasAnyFilters = useMemo(() => {
    return !!(
      (replyStatus && replyStatus !== '') ||
      (createdDateRange?.from && createdDateRange?.to) ||
      (scheduledDateRange?.from && scheduledDateRange?.to) ||
      (sentDateRange?.from && sentDateRange?.to) ||
      (replyDateRange?.from && replyDateRange?.to) ||
      selectedEmailAddresses?.length ||
      selectedNames?.length
    );
  }, [
    replyStatus,
    createdDateRange,
    scheduledDateRange,
    sentDateRange,
    replyDateRange,
    selectedEmailAddresses,
    selectedNames,
  ]);

  const { getAllDraftsByUser, bulkDeleteDrafts, page: draftPage, onPageChange: onDraftPageChange } = useEmailDrafts();
  const {
    getAllScheduledByUser,
    bulkDeleteScheduled,
    page: scheduledPage,
    onPageChange: onScheduledPageChange,
  } = useScheduledEmails();
  const { getAllSentByUser, page: sentPage, onPageChange: onThreadsPageChange } = useEmailThreads();

  // Initial data fetch for all tabs
  useEffect(() => {
    const fetchAllData = async () => {
      if (!hasAnyFilters) {
        await Promise.all([getAllDraftsByUser(), getAllScheduledByUser(), getAllSentByUser()]);
      }
    };
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTab, hasAnyFilters]);

  // Handle pagination changes for individual tabs
  useEffect(() => {
    if (!hasAnyFilters) {
      const fetchDataForTab = async () => {
        switch (emailTab) {
          case 'Drafts':
            await getAllDraftsByUser();
            break;
          case 'Scheduled':
            await getAllScheduledByUser();
            break;
          case 'Sent':
            await getAllSentByUser();
            break;
        }
      };
      fetchDataForTab();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftPage, scheduledPage, sentPage, hasAnyFilters]);

  const handleBulkSelect = (id) => {
    setSelectedEmails((prev) => {
      if (prev.includes(id)) {
        return prev.filter((emailId) => emailId !== id);
      }
      return [...prev, id];
    });
  };

  const handleBulkDelete = async () => {
    if (emailTab === 'Drafts') {
      await bulkDeleteDrafts(selectedEmails);
    } else if (emailTab === 'Scheduled') {
      await bulkDeleteScheduled(selectedEmails);
    }
    setSelectedEmails([]);
  };

  const handleTabChange = (newTab) => {
    setSelectedEmails([]);
    EverTrue.UrlManager.navigate(`/outreach/email/${newTab.toLowerCase()}`, true, false);
  };

  const handleMainTabChange = (tab) => {
    setSelectedEmails([]);
    EverTrue.UrlManager.navigate(`/outreach/${tab}`, true, false);
  };

  const resetPage = () => {
    if (emailTab === 'Drafts') {
      onDraftPageChange(1);
    } else if (emailTab === 'Scheduled') {
      onScheduledPageChange(1);
    } else if (emailTab === 'Sent') {
      onThreadsPageChange(1);
    }
  };

  return (
    <>
      <PageHeader title="Outreach" />
      <OutreachTabGroup activeTab="email" onTabChange={handleMainTabChange} />
      <div className="email-controller">
        <div className="email-header-group">
          <div className="email-header-group-left">
            <EmailTabGroup activeTab={emailTab || 'Drafts'} onTabChange={handleTabChange} />
          </div>
          <div className="email-header-group-right">
            <EmailerDrawerButton />
            {emailTab !== 'Sent' && (
              <ActionsMenu
                options={[
                  {
                    id: `bulk-discard-${emailTab}`,
                    icon: 'delete',
                    label: `Discard ${emailTab}`,
                    disabled: selectedEmails.length === 0,
                    mountOnClick: ({ is_open, close }) => {
                      return (
                        <BulkDeleteEmailModal
                          isOpen={is_open}
                          onClose={close}
                          handleBulkDelete={handleBulkDelete}
                          emailType={emailTab}
                        />
                      );
                    },
                  },
                ]}
              />
            )}
          </div>
        </div>
        <OutreachFilters
          emailTab={emailTab}
          replyStatus={replyStatus}
          setReplyStatus={setReplyStatus}
          resetPage={resetPage}
          createdDateRange={createdDateRange}
          setCreatedDateRange={setCreatedDateRange}
          scheduledDateRange={scheduledDateRange}
          setScheduledDateRange={setScheduledDateRange}
          sentDateRange={sentDateRange}
          setSentDateRange={setSentDateRange}
          replyDateRange={replyDateRange}
          setReplyDateRange={setReplyDateRange}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          selectedEmailAddresses={selectedEmailAddresses}
          setSelectedEmailAddresses={setSelectedEmailAddresses}
          selectedNames={selectedNames}
          setSelectedNames={setSelectedNames}
          setFilterType={setFilterType}
        />

        {emailTab === 'Drafts' && (
          <EmailDraftController
            selectedEmails={selectedEmails}
            handleBulkSelect={handleBulkSelect}
            dateRange={createdDateRange}
            selectedEmailAddresses={selectedEmailAddresses}
            selectedNames={selectedNames}
          />
        )}
        {emailTab === 'Scheduled' && (
          <ScheduledEmailController
            selectedEmails={selectedEmails}
            handleBulkSelect={handleBulkSelect}
            dateRange={scheduledDateRange}
            selectedEmailAddresses={selectedEmailAddresses}
            selectedNames={selectedNames}
          />
        )}
        {emailTab === 'Sent' && (
          <ThreadController
            dateRange={sentDateRange}
            replyStatus={replyStatus}
            replyDateRange={replyDateRange}
            selectedEmailAddresses={selectedEmailAddresses}
            emailRecipients={selectedEmailAddresses}
            selectedNames={selectedNames}
            filterType={filterType}
          />
        )}
      </div>
    </>
  );
};

EmailController.propTypes = {
  emailTab: PropTypes.string,
};

export default EmailController;
